import { useEffect, useRef, useState } from "react";
import { money } from "../../utils/format";
import { isRefused } from "../../services/apiClient";
import { clientsApi, honeyStockApi, poolApi, productsApi, receiptApi, vendorApi } from "../../services/businessApi";
import { useToast } from "../../context/ToastContext";
import { useFeatureTheme } from "../../context/FeatureThemeContext";
import { Card } from "../../components/common/Card";
import { Button } from "../../components/common/Button";
import { Badge } from "../../components/common/Badge";
import { ConfirmDialog } from "../../components/common/ConfirmDialog";
import { DataTable } from "../../components/common/DataTable";
import { Modal } from "../../components/common/Modal";
import { Field } from "../../components/common/Field";
import { SelectOrAdd } from "../../components/common/SelectOrAdd";
import { formatDateOnly } from "../../services/hiveActions";
import { ReceiptView } from "../../components/business/ReceiptView";
import { VendorSummary } from "../../components/business/VendorSummary";
import { ContactLinks } from "../../components/common/ContactLinks";
import { VendorLedger } from "../../components/business/VendorLedger";
import { jarsLabel } from "../../services/bottledStock";
import { generalVarietalId } from "../../components/common/VarietalPicker";
import { useFlow } from "../../hooks/useFlow";
import { FLOWS } from "../../services/flows";

/**
 * One vendor, and everything you do with them (#215).
 *
 * Laid out in the order the user asked for: "their stock, their past
 * orders, any stock they returned, and last money collected date."
 *
 * Stock leads deliberately. Two different conversations happen with a
 * vendor -- settling up ("you owe me $94") and restocking ("you've got 6
 * jars left, want more?") -- and only the money half was answerable
 * before. The balance is still prominent, but what they are holding is
 * what you cannot get from anywhere else.
 */


export function VendorDetail({ clientId, onBack, onChanged }) {
  const { notify } = useToast();
  /* WHICH OTHER PRODUCTS THIS ACCOUNT HAS (#430). The same source the
     Sidebar earns its other-products link from, so the two cannot
     disagree about whether an account sells wax -- and the same rule:
     offered because something was actually recorded, not because the
     feature exists. */
  const { otherProducts } = useFeatureTheme();
  // The receipt currently being shown, if any. Held here rather than routed
  // to, because issuing one happens WHILE somebody is standing there waiting
  // -- a navigation would lose the vendor page behind it (#193).
  const [receipt, setReceipt] = useState(null);
  const [issuing, setIssuing] = useState(null);
  const [data, setData] = useState(null);
  const [sizes, setSizes] = useState([]);
  /* Packaged sizes of wax, propolis and pollen with their shelf counts,
     keyed by product (10 Oct: "I have 2 sizes of wax bars but I can only
     choose Bulk"). Best-effort: without it the dialog offers bulk only. */
  const [otherSizes, setOtherSizes] = useState({});
  // The error itself, or null. See load() -- a refusal is not a failure.
  const [failed, setFailed] = useState(null);

  const [assignOpen, setAssignOpen] = useState(false);
  const [assignForm, setAssignForm] = useState({ mode: "bottled", bottle_size_id: "", bottle_count: "", pounds: "", unit_price: "", notes: "", varietal_id: "" });
  /* "bulk:Wax" and friends carry the product in the mode, so this is the
     one place that decides what the rest of the form asks. Honey keeps
     the varietal question; nothing else has one to answer. */
  const sendingHoney = !assignForm.mode.includes(":");
  /* startsWith, NOT `=== "bulk"`. The mode became "bulk:Wax" for the
     other products, and an equality check here silently labelled a wax
     sale "Price per bottle" -- priced per pound and described per jar on
     the same form. */
  const sendingBulk = assignForm.mode.startsWith("bulk");
  /* What is being sent, for the copy. "Send honey to Corner Store" above
     a wax form is the app not following its own dialog. */
  const sendingProduct = sendingHoney ? "honey" : assignForm.mode.split(":")[1].toLowerCase();
  // Which honey is going out (V063). Business only, like every other
  // varietal control -- an account with one pool has one answer.
  const [varietals, setVarietals] = useState([]);
  const [stock, setStock] = useState([]);
  const [payOpen, setPayOpen] = useState(false);
  const [payForm, setPayForm] = useState({ amount: "", method: "", notes: "" });
  const [returnOpen, setReturnOpen] = useState(false);
  // Jars handed back for a size nobody is counting yet. Held so the page
  // can OFFER to start counting rather than leaving the return to vanish
  // from the shelf figures (25 Aug).
  // The usual price for each size and varietal (V081). Prefills only --
  // whatever the form ends up sending is what gets recorded, so a
  // one-off discount changes that sale and nothing else.
  const [prices, setPrices] = useState({});
  const [uncounted, setUncounted] = useState(null);
  const [returnForm, setReturnForm] = useState({ mode: "bottled", bottle_size_id: "", bottle_count: "", pounds: "", condition: "Good", notes: "", varietal_id: "" });
  const [glassOpen, setGlassOpen] = useState(false);
  // Marking stock SOLD (#257). Bottles only for now: a bulk buyer taking
  // 50 lb is one delivery, and "sell some of it" is not a thing that
  // happens to a mead maker's drum.
  const [soldFor, setSoldFor] = useState(null);
  /* #484. editForm holds "" for an absent contact, and saveVendor turns
     that back into null -- an empty string would store "their email is
     blank" rather than "we do not have one". */
  const [editing, setEditing] = useState(false);
  const [savingVendor, setSavingVendor] = useState(false);
  const [editForm, setEditForm] = useState({ name: "", contact_email: "", contact_phone: "" });
  /* #484 follow-up. The vendor list is loaded ON DEMAND rather than with
     the page: merging is rare, and every vendor page paying for a second
     request to support it would be the wrong trade. */
  const [mergeInto, setMergeInto] = useState("");
  const [mergeCandidates, setMergeCandidates] = useState(null);
  const [merging, setMerging] = useState(false);
  const [confirmingMerge, setConfirmingMerge] = useState(false);
  /* Named rather than looked up inside the dialog, so the copy reads
     the same whether or not the list has been reloaded underneath. */
  const mergeTargetName = (mergeCandidates || [])
    .find((c) => String(c.client_id) === String(mergeInto))?.name || "";
  // #379, keyed to the Sold dialog. Browsing a vendor is not an
  // abandoned sale.
  const saleFlow = useFlow(FLOWS.SALE, soldFor !== null);
  // The last figure WE put in the collected box, so a later change to
  // the quantity can update it while a figure the user typed is left
  // alone.
  const autoCollected = useRef("");
  const [soldCount, setSoldCount] = useState("");
  // Money handed over at the same moment. A PAYMENT against what they
  // already owe -- never a price on the sale, which would bill the same
  // jars twice (the debt was created when the honey went out).
  const [soldCollected, setSoldCollected] = useState("");
  const [soldMethod, setSoldMethod] = useState("");
  const [glassForm, setGlassForm] = useState({ bottle_size_id: "", bottle_count: "", note: "" });

  async function load() {
    try {
      const [d, s] = await Promise.all([vendorApi.detail(clientId), poolApi.listSizes()]);
      setData(d);
      setSizes(s.bottle_sizes || []);
      setFailed(null);
      try {
        const o = await productsApi.list();
        setOtherSizes(Object.fromEntries((o.products || []).map((p) => [
          p.product_type, (p.sizes || []).filter((z) => z.is_active !== false)])));
      } catch { setOtherSizes({}); }
    } catch (err) {
      // Keep the ERROR, not a boolean. A refusal and a dead connection need
      // different words and different buttons, and `failed: true` could not
      // tell them apart (#245).
      setFailed(err);
    }
  }
  useEffect(() => { load(); }, [clientId]); // eslint-disable-line react-hooks/exhaustive-deps

  // Best-effort and on its own: an account without varietals gets a 403
  // here, and a vendor page must not fail to load over a picker it was
  // never going to show.
  useEffect(() => {
    let cancelled = false;
    honeyStockApi.listVarietals()
      .then((r) => {
        if (cancelled) return;
        const list = r.varietals || [];
        setVarietals(list);
        /* LAND ON GENERAL, the way the phone's Send Honey sheet already
           did (#391). Web asked "Choose…" and refused to submit without
           an answer, so the commonest account there is -- ordinary
           honey, no varietals kept -- had to answer a question with one
           real answer, every delivery, on the desk but not on the phone.

           Found BY NAME rather than assumed to be null: General is a row
           with an id, and that id is what a saved price is keyed by. An
           account with no General row keeps the placeholder, which is
           the old behaviour rather than a wrong guess. */
        const general = generalVarietalId(list);
        if (general != null) {
          setAssignForm((f) => (f.varietal_id ? f : { ...f, varietal_id: String(general) }));
        }
      })
      .catch(() => !cancelled && setVarietals([]));
    // The usual prices (V081). Best-effort, like the varietals: without
    // them the forms simply ask for a price, which is what they did
    // before, and a failure here must not break sending honey.
    honeyStockApi.bottlePrices()
      .then((r) => {
        if (cancelled) return;
        const map = {};
        for (const row of r.prices || []) {
          map[`${row.bottle_size_id}-${row.varietal_id}`] = Number(row.unit_price);
        }
        setPrices(map);
      })
      .catch(() => !cancelled && setPrices({}));
    // What is actually on the shelf, per varietal and size. The picker
    // listed every varietal the account has ever named, with no hint of
    // how many jars were behind each -- so the first sign of sending more
    // than exists was the refusal after submitting. Same figure the guard
    // enforces, so the two cannot disagree.
    poolApi.get()
      .then((r) => !cancelled && setStock(r.bottled_by_varietal || []))
      .catch(() => !cancelled && setStock([]));
    return () => { cancelled = true; };
  }, []);

  function afterChange() {
    load();
    // The list behind this view shows balances, so it has to refresh too --
    // otherwise going back shows a figure this page just disproved.
    onChanged?.();
  }

  async function issueFor(type, id) {
    setIssuing(id);
    try {
      const issued = await receiptApi.issue(
        type === "Delivery" ? { receipt_type: "Delivery", assignment_id: id }
                            : { receipt_type: "Payment", payment_id: id },
      );
      setReceipt(issued);
    } catch (err) {
      // The sale is untouched by this failing -- say so, or somebody will
      // wonder whether the delivery itself went missing.
      notify(err.apiMessage || "Couldn't make a receipt just now — the order itself is still recorded", "danger");
    } finally {
      setIssuing(null);
    }
  }

  async function saveVendor() {
    const name = editForm.name.trim();
    if (!name) return notify("A vendor needs a name", "warning");
    setSavingVendor(true);
    try {
      /* EMPTY BECOMES NULL, which is what clears a wrong number. Sending
         "" would store a blank value -- "their email is blank" rather
         than "we do not have one" -- and the two read differently to
         anybody trying to contact them. */
      await clientsApi.update(clientId, {
        name,
        contact_email: editForm.contact_email.trim() || null,
        contact_phone: editForm.contact_phone.trim() || null,
      });
      notify("Vendor updated", "success");
      setEditing(false);
      await load();
      onChanged?.();
    } catch (err) {
      notify(err.apiMessage || "Could not update that vendor", "danger");
    } finally {
      setSavingVendor(false);
    }
  }

  async function loadMergeCandidates() {
    try {
      const r = await clientsApi.list({ limit: 200 });
      /* NOT THIS VENDOR. Merging a vendor into itself would retire the
         only copy and move its ledger onto a row being put away in the
         same breath -- the server refuses it, and offering it here would
         be a trap rather than a safeguard. */
      setMergeCandidates((r.clients || []).filter((c) => c.client_id !== clientId));
    } catch {
      setMergeCandidates([]);
    }
  }

  async function doMerge() {
    const target = Number(mergeInto);
    if (!target) return notify("Pick the vendor to keep", "warning");
    setMerging(true);
    try {
      const r = await clientsApi.merge(clientId, target);
      /* SAYS WHAT MOVED, including nothing. A duplicate that was never
         used is a different situation from one that was, and the owner
         is the only person who can tell whether that is a surprise. */
      const detail = (r.moved || []).length
        ? (r.moved || []).map((m) => `${m.rows_moved} ${m.what}`).join(", ")
        : "nothing was filed under it";
      notify(`${r.merged} is now ${r.into} — ${detail}.`, "success");
      onChanged?.();
      onBack?.();
    } catch (err) {
      notify(err.apiMessage || "Could not merge those vendors", "danger");
    } finally {
      setMerging(false);
    }
  }

  async function retireVendor(putAway) {
    setSavingVendor(true);
    try {
      await clientsApi.update(clientId, { is_active: !putAway });
      /* SAYS WHAT SURVIVES. Somebody putting a vendor away is entitled
         to wonder whether they have just deleted a year of invoices. */
      notify(
        putAway
          ? "Put away. They will not be offered on new deliveries, and their history is untouched."
          : "Brought back.",
        "success",
      );
      await load();
      onChanged?.();
    } catch (err) {
      notify(err.apiMessage || "Could not change that vendor", "danger");
    } finally {
      setSavingVendor(false);
    }
  }

  async function submitAssign() {
    /* The mode is "bottled", "bulk", or "bulk:Wax" and friends (#430).
       One control rather than two, because the question a seller is
       answering is a single one -- what am I sending -- and splitting it
       into "which product" plus "bottled or bulk" would offer bottled
       wax, which this slice does not do. */
    const [form, product] = assignForm.mode.split(":");
    const bulk = form === "bulk";
    const productType = product || "Honey";
    const isHoney = productType === "Honey";
    const payload = {
      client_id: clientId,
      unit_price: Number(assignForm.unit_price || 0),
      notes: assignForm.notes || undefined,
      /* Omitted for honey, so nothing about the existing requests
         changes -- the server defaults to Honey and every caller written
         before this meant it. */
      ...(isHoney ? {} : { product_type: productType }),
      // NULL is NOT a real answer any more. The server resolves an absent
      // varietal to General exactly as the pool does, and checks that
      // varietal's own shelf before writing -- so an unset picker no
      // longer means "unattributed", it means General. The guard below
      // still asks rather than letting the form default past the
      // question where there is a real choice to make.
      /* NEVER for anything but honey. A varietal is the flower the
         nectar came from, so wax cannot have one -- and the server
         REFUSES the pair rather than ignoring it, so sending a stale
         picker value here would fail the sale outright. */
      varietal_id: isHoney && assignForm.varietal_id
        ? Number(assignForm.varietal_id) : undefined,
      ...(bulk
        ? { pounds: Number(assignForm.pounds) }
        : { bottle_size_id: Number(assignForm.bottle_size_id), bottle_count: Number(assignForm.bottle_count) }),
    };
    // `> 0`, not falsy: -5 is truthy, so a negative reached the server to
    // be refused by the schema rather than answered by the form.
    if (bulk ? !(payload.pounds > 0)
             : !(payload.bottle_size_id && payload.bottle_count > 0)) {
      return notify(bulk ? "Enter how many pounds" : "Pick a size and how many bottles", "warning");
    }
    // Bulk now comes out of ONE varietal's balance (25 Aug), so leaving
    // the picker blank means selling from the default -- and if the
    // default holds nothing, the server refuses with a number the seller
    // cannot place. Asking here is clearer than explaining there.
    // Bulk AND bottled (28 Aug). "Not separated" was still reachable on a
    // bottled assignment, which writes varietal_id NULL -- the bucket
    // V080 was written to drain. Draining it and leaving the tap running
    // would just refill it.
    /* ASKED ONLY OF HONEY. Demanding a varietal for a wax sale would
       make the form refuse a request the server would have accepted, and
       ask a question with no true answer to unblock it. */
    if (isHoney && varietals.length > 0 && !payload.varietal_id) {
      return notify(
        bulk ? "Which honey is being sold? Bulk comes out of one varietal."
             : "Which honey is going out? Pick a varietal so the stock comes off the right one.",
        "warning",
      );
    }
    try {
      const r = await vendorApi.assign(payload);
      notify(`Sent out — ${money(r.value)} owed`, "success");
      setAssignOpen(false);
      setAssignForm({ mode: "bottled", bottle_size_id: "", bottle_count: "", pounds: "", unit_price: "", notes: "", varietal_id: "" });
      afterChange();
    } catch (err) {
      notify(err.apiMessage || "Could not record the delivery", "danger");
    }
  }

  async function submitPay() {
    const amount = Number(payForm.amount);
    if (!amount) return notify("Enter an amount", "warning");
    try {
      await vendorApi.pay({ client_id: clientId, amount, method: payForm.method || undefined, notes: payForm.notes || undefined });
      notify(`Recorded ${money(amount)}`, "success");
      setPayOpen(false);
      setPayForm({ amount: "", method: "", notes: "" });
      afterChange();
    } catch (err) {
      notify(err.apiMessage || "Could not record the payment", "danger");
    }
  }

  const priceKey = (sizeId, varietalId) => `${sizeId}-${varietalId}`;

  /*
   * Fill the price in whenever the pair is known (V081, fixed 28 Aug).
   *
   * This was hooked onto the varietal dropdown's onChange, which only
   * fires if the varietal is the LAST thing chosen. Pick the size after
   * the varietal, reopen the dialog, or let a size default in, and
   * nothing filled -- so a remembered price existed and was never used.
   * An effect on the pair covers every order.
   *
   * It never OVERWRITES a price already typed: somebody may be giving
   * this vendor a deal, and having their number snap back would be worse
   * than no prefill at all.
   */
  useEffect(() => {
    const usual = usualPrice(Number(assignForm.bottle_size_id), Number(assignForm.varietal_id));
    if (usual == null || assignForm.unit_price) return;
    setAssignForm((f) => ({ ...f, unit_price: String(usual) }));
  }, [assignForm.bottle_size_id, assignForm.varietal_id, prices]); // eslint-disable-line react-hooks/exhaustive-deps

  /* A non-honey size's default price (#520), set in Manage sizes. Fills the
     price when a packaged wax/propolis/pollen size is picked. Replaces a
     price only if it is empty or is the one this put there for the previous
     size; anything the keeper typed is left alone. */
  const autoPrice = useRef("");
  useEffect(() => {
    const product = assignForm.mode.startsWith("bottled:") ? assignForm.mode.split(":")[1] : null;
    if (!product || !assignForm.bottle_size_id) return;
    const size = (otherSizes[product] || []).find(
      (z) => String(z.bottle_size_id) === String(assignForm.bottle_size_id));
    if (size?.default_price == null) return;
    const prev = autoPrice.current;  // read now: the updater below runs later
    setAssignForm((f) => (f.unit_price && f.unit_price !== prev ? f
      : { ...f, unit_price: String(size.default_price) }));
    autoPrice.current = String(size.default_price);
  }, [assignForm.mode, assignForm.bottle_size_id, otherSizes]);

  /*
   * The Sold dialog collects what was COLLECTED, not a price -- so the
   * useful default is the arithmetic somebody is doing in their head at
   * a counter: jars x the usual price.
   */
  useEffect(() => {
    if (!soldFor) return;
    const usual = usualPrice(Number(soldFor.bottle_size_id), Number(soldFor.varietal_id));
    const count = Number(soldCount);
    if (usual == null || !count) return;

    // Follows the count until somebody TYPES a figure (reported 28 Aug:
    // change the quantity and the money stayed put). The distinction is
    // between a number we filled in -- ours to keep correct -- and one
    // the user chose, which must never move under them.
    //
    // Compared against what we last computed rather than a flag: a flag
    // has to be reset in every path that opens or closes the dialog, and
    // the one path somebody forgets is where it goes wrong.
    const next = (count * usual).toFixed(2);
    if (soldCollected === "" || soldCollected === autoCollected.current) {
      autoCollected.current = next;
      setSoldCollected(next);
    }
  }, [soldFor, soldCount, prices]); // eslint-disable-line react-hooks/exhaustive-deps

  /** The remembered price, or null. NEVER 0 as a stand-in for "unknown":
   *  a form prefilled with 0 has told somebody a price, and 0 is a real
   *  answer somebody may have chosen. */
  function usualPrice(sizeId, varietalId) {
    if (!sizeId || !varietalId) return null;
    const known = prices[priceKey(sizeId, varietalId)];
    return known == null ? null : known;
  }

  async function startCountingReturned() {
    try {
      await vendorApi.trackGlass({
        bottle_size_id: uncounted.bottle_size_id,
        // The jars we just watched come back, as DIRTY -- they came off a
        // shop shelf and are no use until washed.
        dirty_count: uncounted.count,
        // Zero CLEAN is a claim, not a fact: there may be crates in the
        // barn. Said plainly in the offer so nobody reads it as a count
        // the app took.
        clean_count: 0,
      });
      notify(
        `Counting ${uncounted.label} jars now — ${uncounted.count} dirty. ` +
        "Set your clean count on the Honey Pool page if you have any.",
        "success",
      );
      setUncounted(null);
    } catch (err) {
      notify(err.apiMessage || "Couldn't start counting those", "danger");
    }
  }

  async function submitGlass() {
    const count = Number(glassForm.bottle_count);
    // `count > 0`, not `!count`: -5 is truthy and would reach the server.
    if (!glassForm.bottle_size_id || !(count > 0)) return notify("Pick a size and how many jars came back", "warning");
    try {
      const r = await vendorApi.recordGlass({
        client_id: clientId,
        bottle_size_id: Number(glassForm.bottle_size_id),
        bottle_count: count,
        note: glassForm.note || undefined,
      });
      // Says what did NOT happen, deliberately. The neighbouring Accept
      // Return credits the vendor, and a user who confuses the two would
      // otherwise assume this had too.
      //
      // And now says whether the jars reached the glass count. They only
      // do when this account is counting that size -- opting in is the
      // whole design -- but saying nothing made a logged return look like
      // a broken one (reported 25 Aug).
      const base = `Logged ${r.bottle_count} x ${r.bottle_size_label} back. They still owe what they owed.`;
      notify(base, "success");
      // Not a failure -- counting glass is opt-in and most people never
      // do. But the jars are real and on a shelf somewhere, so the page
      // offers to start counting instead of quietly dropping them.
      setUncounted(
        r.counted_as_dirty === false
          ? { bottle_size_id: Number(glassForm.bottle_size_id),
              label: r.bottle_size_label, count: r.bottle_count }
          : null,
      );
      setGlassOpen(false);
      setGlassForm({ bottle_size_id: "", bottle_count: "", note: "" });
      onChanged?.();
      load();
    } catch (err) {
      notify(err.apiMessage || "Could not log those jars", "danger");
    }
  }

  async function submitReturn() {
    const bulk = returnForm.mode === "bulk";
    const payload = {
      client_id: clientId,
      condition: returnForm.condition,
      notes: returnForm.notes || undefined,
      // Which honey came back (27 Aug). Without it a loose return landed
      // with no varietal at all and credited the stock to "Unspecified"
      // instead of to what it actually was.
      varietal_id: returnForm.varietal_id ? Number(returnForm.varietal_id) : undefined,
      ...(bulk
        ? { pounds: Number(returnForm.pounds) }
        : { bottle_size_id: Number(returnForm.bottle_size_id), bottle_count: Number(returnForm.bottle_count) }),
    };
    if (bulk ? !payload.pounds : !(payload.bottle_size_id && payload.bottle_count)) {
      return notify(bulk ? "Enter how many pounds came back" : "Pick a size and how many came back", "warning");
    }
    try {
      const r = await vendorApi.accept(payload);
      notify(
        returnForm.condition === "Damaged"
          ? `${money(r.credited)} off their account. Damaged stock was not returned to inventory.`
          : `${money(r.credited)} off their account, back in stock.`,
        "success",
      );
      setReturnOpen(false);
      setReturnForm({ mode: "bottled", bottle_size_id: "", bottle_count: "", pounds: "", condition: "Good", notes: "", varietal_id: "" });
      afterChange();
    } catch (err) {
      notify(err.apiMessage || "Could not record the return", "danger");
    }
  }

  if (failed) {
    // A 403 is the server's answer, not a hiccup. Offering "Try again"
    // against one produces the same refusal forever and sends the user to
    // check a connection that is fine -- so the retry is not offered, and
    // the server's own message (which names the plan) is shown instead of
    // a generic failure (#245).
    //
    // Since #254 both the list and this page are HoneyChain, and what the
    // Business plan buys is the AMOUNTS, not the page -- so a refusal here
    // means the honey chain itself, which is a different sentence.
    const refused = isRefused(failed);
    return (
      <Card>
        <p>
          {refused
            ? failed.apiMessage || "Vendors are part of the Harvester plan."
            : "Couldn't load this vendor. Check your connection and try again."}
        </p>
        {refused ? null : <Button onClick={load}>Try again</Button>}
        <Button variant="secondary" onClick={onBack}>Back to vendors</Button>
      </Card>
    );
  }
  if (!data) return <Card><p>Loading...</p></Card>;

  const owed = Number(data.outstanding || 0);
  // #235: the server omits the money keys entirely for a member the owner
  // has hidden them from. Checked by PRESENCE, not by truthiness -- a
  // genuine outstanding of 0 is a settled vendor and must still show.
  const showMoney = "outstanding" in data;
  // "shown" | "plan" | "hidden" -- the server says WHY the amounts are
  // missing so the summary can offer an upgrade to the tier that would fix
  // it, and stay quiet when an upgrade would not (#235).
  const moneyVisibility = data.money_visibility;
  /* The Inventory card's sections (#522): one per product, honey first.
     A holding with no product_type is honey (an older API build). */
  const inventorySections = (() => {
    const by = {};
    const add = (product) => (by[product] ||= { product, rows: [], bulk: 0 });
    (data.holdings || []).forEach((h) => add(h.product_type || "Honey").rows.push(h));
    if (Number(data.bulk_pounds_held || 0) > 0) add("Honey").bulk = Number(data.bulk_pounds_held);
    (data.other_products || []).forEach((o) => {
      if (Number(o.bulk_pounds_held || 0) > 0) add(o.product_type).bulk = Number(o.bulk_pounds_held);
    });
    const order = ["Honey", "Wax", "Propolis", "Pollen"];
    const list = Object.values(by).sort((x, y) => order.indexOf(x.product) - order.indexOf(y.product));
    return list.map((sec, i) => ({ ...sec, first: i === 0 }));
  })();
  const holdsAnything = inventorySections.length > 0;

  /**
   * They sold it, so it comes off their shelf (#257).
   *
   * Reported as "you can send them honey, you can return their honey to
   * stock, but there's no way to mark the bottles sold, it just collects
   * the money and leaves the bottles."
   *
   * Deliberately does NOT touch the balance. The debt was created when the
   * honey was assigned, so a sale only says where the jars went -- the API
   * carries no price for exactly that reason.
   */
  async function recordSale() {
    const count = Number(soldCount);
    if (!count) {
      notify("How many did they sell?", "warning");
      return;
    }
    try {
      const collected = Number(soldCollected);
      // Built up rather than sent with undefined holes: a payload whose
      // shape says what happened is easier to read in a log, and keeps
      // "no money changed hands" distinguishable from "zero was taken".
      const payload = {
        client_id: clientId,
        bottle_size_id: soldFor.bottle_size_id,
        bottle_count: count,
      };
      if (soldFor.varietal_id) payload.varietal_id = soldFor.varietal_id;
      if (collected > 0) {
        payload.amount_collected = collected;
        if (soldMethod) payload.payment_method = soldMethod;
      }
      const result = await vendorApi.recordSale(payload);
      /* #379. "Is the business side used at all by the accounts paying
         for it" is the live question (#378's usage-based trial exit
         needs exactly this), and a sale is the answer. */
      saleFlow.finished();
      // Two different things happened, so say which. The server reports
      // whether the payment landed separately, because the sale can
      // succeed while the payment write fails and telling somebody their
      // money is recorded when it is not is the worse error.
      if (collected > 0 && result?.payment_recorded === false) {
        notify(
          `${count} x ${soldFor.label} marked sold, but the payment didn't save — add it on the payments form.`,
          "warning",
        );
      } else if (collected > 0) {
        notify(`${count} x ${soldFor.label} sold, $${collected.toFixed(2)} collected`, "success");
      } else {
        notify(`${count} x ${soldFor.label} marked sold`, "success");
      }
      setSoldFor(null);
      setSoldCount("");
      setSoldCollected("");
      setSoldMethod("");
      load();
      onChanged?.();
    } catch (err) {
      // The server names how many they actually hold.
      notify(err.apiMessage || "Could not record that sale", "danger");
    }
  }

  return (
    <div>
      {uncounted && (
        <div style={{
          border: "1px solid var(--color-border)", borderRadius: "8px",
          padding: "var(--space-3)", marginBottom: "var(--space-3)",
        }}>
          <strong>{uncounted.count} x {uncounted.label} came back.</strong>{" "}
          You&apos;re not counting {uncounted.label} jars yet, so your shelf
          figures haven&apos;t changed.
          <div style={{ display: "flex", gap: "var(--space-2)", marginTop: "var(--space-2)", flexWrap: "wrap" }}>
            <Button size="sm" onClick={startCountingReturned}>
              Start counting — {uncounted.count} dirty
            </Button>
            <Button size="sm" variant="ghost" onClick={() => setUncounted(null)}>
              No thanks
            </Button>
          </div>
          <p style={{ margin: "var(--space-2) 0 0", fontSize: "var(--font-size-sm)", color: "var(--color-text-muted)" }}>
            We&apos;ll start you at 0 clean. If you have clean jars in the barn,
            set the real number on the Honey Pool page — a wrong 0 is worse
            than no count at all.
          </p>
        </div>
      )}
      <Button variant="secondary" onClick={onBack}>← All vendors</Button>
      {/* EDIT IN PLACE (#484). Until this existed app.Clients had no
          update path at all -- a typo'd name, a wrong phone number and
          an unreachable email were permanent, from either client.

          Here rather than on the list: this is the screen that already
          shows the name as a heading and the contact beneath it, so it
          is where somebody notices they are wrong. */}
      {editing ? (
        <Card title="Edit vendor" className="section-spacing">
          <div style={{ display: "grid", gap: "var(--space-3)" }}>
            <Field label="Name" required>
              {(id, aria) => (
                <input id={id} {...aria} className="touch-target" value={editForm.name}
                  maxLength={200}
                  onChange={(e) => setEditForm((f) => ({ ...f, name: e.target.value }))} />
              )}
            </Field>
            <Field label="Email" hint="Clear it to remove a wrong one.">
              {(id, aria) => (
                <input id={id} {...aria} className="touch-target" type="email"
                  value={editForm.contact_email} maxLength={256}
                  onChange={(e) => setEditForm((f) => ({ ...f, contact_email: e.target.value }))} />
              )}
            </Field>
            <Field label="Phone" hint="Clear it to remove a wrong one.">
              {(id, aria) => (
                <input id={id} {...aria} className="touch-target" value={editForm.contact_phone}
                  maxLength={32}
                  onChange={(e) => setEditForm((f) => ({ ...f, contact_phone: e.target.value }))} />
              )}
            </Field>
            <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap" }}>
              <Button onClick={saveVendor} disabled={savingVendor || !editForm.name.trim()}>
                {savingVendor ? "Saving…" : "Save"}
              </Button>
              <Button variant="secondary" onClick={() => setEditing(false)}>Cancel</Button>
            </div>

            {/*
              * THE TWO CONSEQUENTIAL ONES, ON THEIR OWN (#501).
              *
              * Raised 6 Oct with a suggestion to move retire up beside
              * Edit. The opposite, for the reason the suggestion implies:
              * Edit and Save are pressed constantly, retiring a vendor
              * with a payment history is rare, and adjacency is what
              * invites the mis-click. So these sit below a rule, away
              * from Save.
              *
              * "RETIRE", NOT "PUT AWAY". Put away is the word for a
              * PICKER OPTION -- a varietal, a jar size, an equipment
              * type -- where it accurately describes no longer being
              * offered. A vendor is a record with a ledger, and you do
              * not put a shop away. The app now carries two words for a
              * similar lifecycle, which is worth stating plainly -- but
              * "put away this shop" was the stranger of the two
              * readings.
              *
              * STILL NOT "DELETE" OR "REMOVE". A client_id is referenced
              * by assignments, returns, sales and payments, so V126
              * retires rather than deletes -- and a word implying
              * deletion would be a promise the code does not keep, which
              * is the same reason #410 refused "Delete" for jar sizes.
              */}
            <div style={{
              marginTop: "var(--space-3)",
              paddingTop: "var(--space-3)",
              borderTop: "1px solid var(--color-border)",
              display: "flex", gap: "var(--space-2)", flexWrap: "wrap",
            }}>
              <Button
                variant="secondary"
                onClick={() => retireVendor(!(data.vendor.is_active === false))}
                disabled={savingVendor}
              >
                {data.vendor.is_active === false ? "Restore vendor" : "Retire vendor"}
              </Button>
              {/* MERGE, behind its own reveal (#484 follow-up). It is the
                  one control here that moves money between ledgers, and
                  a dropdown sitting open beside Save invites the press
                  nobody meant. */}
              {mergeCandidates === null ? (
                <Button variant="secondary" onClick={loadMergeCandidates}>
                  Merge into another vendor
                </Button>
              ) : null}
            </div>
            {mergeCandidates !== null ? (
              <div style={{ display: "grid", gap: "var(--space-2)" }}>
                <Field
                  label="Keep which vendor?"
                  hint={`Everything filed under ${data.vendor.name} moves across, and ${data.vendor.name} is put away.`}
                >
                  {(id, aria) => (
                    <select id={id} {...aria} className="touch-target" value={mergeInto}
                      onChange={(e) => setMergeInto(e.target.value)}>
                      <option value="">Choose…</option>
                      {mergeCandidates.map((c) => (
                        <option key={c.client_id} value={c.client_id}>{c.name}</option>
                      ))}
                    </select>
                  )}
                </Field>
                <div style={{ display: "flex", gap: "var(--space-2)" }}>
                  <Button onClick={() => setConfirmingMerge(true)} disabled={merging || !mergeInto}>
                    {merging ? "Moving records…" : "Merge"}
                  </Button>
                  <Button variant="secondary" onClick={() => setMergeCandidates(null)}>
                    Cancel
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        </Card>
      ) : null}
      <h1>
        {data.vendor.name}
        {data.vendor.is_active === false ? (
          <>{" "}<Badge tone="neutral">put away</Badge></>
        ) : null}
      </h1>
      {editing ? null : (
        <Button size="sm" variant="secondary" onClick={() => {
          setEditForm({
            name: data.vendor.name || "",
            contact_email: data.vendor.contact_email || "",
            contact_phone: data.vendor.contact_phone || "",
          });
          setEditing(true);
        }}>
          Edit vendor
        </Button>
      )}
      {/* THE NUMBER YOU RING WHEN A DROP IS DUE (#340, web half).
          The endpoint has always returned contact_email and
          contact_phone, mobile has shown them since #340, and web
          rendered them nowhere -- so a shop's number could be typed into
          the app and never read back from the desk. */}
      <ContactLinks phone={data.vendor.contact_phone} email={data.vendor.contact_email} />

      {/* The answer before the working (#254): what they hold, what they
          owe, and what came back, above the history that explains it. */}
      <VendorSummary summary={data.summary} moneyVisibility={moneyVisibility} />

      {/* "Vendor Inventory" (#521). Was "Vendor Honey Inventory" (device
          round, 20 Sep) until the card could hold wax, propolis and
          pollen. Naming the contents beats naming the relationship, and
          now the contents are several products.

          ONE HEADED SECTION PER PRODUCT (#522). "11 x 2 lb Block" said
          nothing about being wax. Every row sits under its product's
          header, even when the vendor holds only one product, so no row
          is ever left to be guessed at. */}
      <Card title="Vendor Inventory">
        {holdsAnything ? (
          <div>
            {inventorySections.map((sec) => (
              <section key={sec.product} aria-label={`${sec.product} inventory`}
                style={{ marginTop: sec.first ? 0 : "var(--space-4)" }}>
                <h3 style={{ margin: 0, paddingBottom: "var(--space-1)",
                             borderBottom: "1px solid var(--color-border)",
                             fontSize: "var(--font-size-md)" }}>
                  {sec.product}
                </h3>
                <ul style={{ listStyle: "none", margin: 0, padding: 0 }}>
                  {sec.rows.map((h) => (
                    // Keyed on size AND varietal: one size can appear more
                    // than once, six Wildflower beside six Clover.
                    <li key={`${h.bottle_size_id}-${h.varietal_id ?? "none"}`}
                        style={{ fontSize: "var(--font-size-lg)", display: "flex", alignItems: "center",
                                 gap: "var(--space-3)", padding: "var(--space-1) 0" }}>
                      <span style={{ flexGrow: 1 }}>
                        <strong>{h.bottles}</strong> x {h.label}
                        {/* Which honey (V071). Omitted where the account
                            never separated varietals. */}
                        {h.varietal_name && h.varietal_name !== "Not separated" && (
                          <span style={{ color: "var(--color-text-muted)" }}> · {h.varietal_name}</span>
                        )}
                      </span>
                      <Button size="sm" variant="secondary"
                        onClick={() => { setSoldFor(h); setSoldCount(String(h.bottles)); }}>
                        Sold
                      </Button>
                    </li>
                  ))}
                  {sec.bulk > 0 ? (
                    <li style={{ fontSize: "var(--font-size-lg)", padding: "var(--space-1) 0" }}>
                      <strong>{sec.bulk}</strong> lb bulk
                    </li>
                  ) : null}
                </ul>
              </section>
            ))}
          </div>
        ) : (
          <p style={{ color: "var(--color-text-muted)" }}>Nothing out with them right now.</p>
        )}

        <div style={{ display: "flex", gap: "var(--space-2)", flexWrap: "wrap", marginTop: "var(--space-3)" }}>
          {/*
            * NOT "Send Honey" WHEN IT SENDS MORE THAN HONEY (#494).
            *
            * Reported 6 Oct: "the button says 'Send Honey' but it's
            * offering Bulk of each so it should probably say 'Send
            * Product'."
            *
            * The dialog behind this has been product-aware since #430 --
            * `sendingProduct` above exists so its copy does not say
            * honey over a wax form -- and the button that opens it was
            * the one place still naming honey.
            *
            * CONDITIONAL, because most accounts sell honey and nothing
            * else. "Send Product" is vaguer than this app usually is, so
            * it is used only where the vaguer word is the accurate one:
            * an account with no wax, propolis or pollen still gets the
            * specific label, since for them the dialog really does send
            * only honey.
            */}
          <Button onClick={() => setAssignOpen(true)}>
            {/* OPTIONAL CHAINING, and it earned itself immediately.
                 This label reads otherProducts on EVERY render, where the
                 `.map` below only ran once the dialog was open -- so
                 moving the read earlier turned a context without that key
                 into a white screen on the whole vendor page. CI caught
                 it from VendorsPage's suite, which navigates here.
                 The real context always supplies [], so this guards
                 against a partial one rather than a real state. */}
            {otherProducts?.length ? "Send Product" : "Send Honey"}
          </Button>
          <Button variant="secondary" onClick={() => setPayOpen(true)}>Record Payment</Button>
          {/* Only offered when they actually hold something -- accepting a
              return from a vendor with nothing out is always a mistake, and
              the API refuses it anyway. */}
          <Button variant="secondary" onClick={() => setReturnOpen(true)} disabled={!holdsAnything}>
            Accept Return
          </Button>
          {/* NOT disabled on holdings, unlike Accept Return. Glass
              circulates for years -- a vendor can hand back jars from
              deliveries older than this system, and refusing those would
              make the log wrong to protect a number that does not exist. */}
          <Button variant="secondary" onClick={() => setGlassOpen(true)}>Log Empty Jars</Button>
        </div>
      </Card>

      {/* Reported 28 Aug: these were touching. */}
      <div style={{ height: "var(--space-5)" }} />

      {/* One list, not three (#254). Deliveries, returns, payments, empty
          glass and sales in the order they happened, because that is the
          only order in which they answer "why does this vendor owe me
          this much". The three tables this replaces each told a quarter of
          the story and left the reader to interleave them.

          The Receipt action moved here with the deliveries rather than
          being dropped -- it is offered only where amounts are, since a
          receipt without amounts is not a receipt. */}
      <VendorLedger
        transactions={data.transactions}
        onIssueReceipt={showMoney ? (sourceId, kind) => issueFor(kind === "Payment" ? "Payment" : "Delivery", sourceId) : undefined}
        issuing={issuing}
      />

      {/* Shown immediately after issuing: the beekeeper is at a stall with
          somebody waiting, so the document appears rather than a "receipt
          created" toast they would then have to go and find. */}
      <Modal
        open={Boolean(receipt)}
        onClose={() => setReceipt(null)}
        title={receipt ? `Receipt ${receipt.receipt_number}` : "Receipt"}
      >
        {receipt && <ReceiptView receipt={receipt} onClose={() => setReceipt(null)} />}
      </Modal>

      <Modal open={assignOpen} onClose={() => setAssignOpen(false)}
        title={`Send ${sendingProduct} to ${data.vendor.name}`}
        footer={<Button onClick={submitAssign}>Record Delivery</Button>}>
        <div style={{ display: "grid", gap: "var(--space-3)" }}>
          {/* Bottled and bulk in one place, per the user: "It feels smoother
              to have all the Honey dispensing in 1 place." Bulk skips
              bottling entirely -- it never becomes a jar. */}
          <Field label="What are you sending?">
            {(id, aria) => (
              <select id={id} {...aria} value={assignForm.mode}
                onChange={(e) => setAssignForm((f) => ({ ...f, mode: e.target.value, bottle_size_id: "" }))} className="touch-target">
                <option value="bottled">Bottled honey</option>
                <option value="bulk">Bulk honey, by the pound</option>
                {/* BULK WAX, PROPOLIS AND POLLEN (#430), asked for on
                    4 Oct: "both should also support the bulk purchase by
                    the lb."

                    Only the products this account actually has, which is
                    the same rule the other-products page and the sidebar
                    follow -- an account that has never recorded a wax
                    harvest is offered nothing about wax. Offering all
                    three to everybody would put two dead options in the
                    commonest dialog in the business tab.

                    BULK ONLY. A bottled wax sale needs the shelf
                    questions this dialog asks of honey -- which size,
                    how many, priced per unit -- and that is the next
                    slice rather than a half-answered option here. */}
                {(otherProducts || []).flatMap((product) => [
                  <option key={product} value={`bulk:${product}`}>
                    {`Bulk ${product.toLowerCase()}, by the pound`}
                  </option>,
                  /* PACKAGED, by the sizes the keeper defined. Only when
                     the product has a size to send. */
                  ...((otherSizes[product] || []).length > 0 ? [
                    <option key={`pkg-${product}`} value={`bottled:${product}`}>
                      {`Packaged ${product.toLowerCase()}, by the size`}
                    </option>,
                  ] : []),
                ])}
              </select>
            )}
          </Field>
          {/* Which honey is going out (V063). Reported as "the varietals
              are not available to the vendors" -- a jar's varietal was
              known on the shelf and forgotten the moment it left.

              Sits here, above the quantity, because it belongs with the
              other questions about WHAT is going out rather than after
              the ones about how much: reported as "the placement of the
              dropdown AFTER the quantity is awkward", and it was.

              Shown only where varietals exist, and it opens on the
              account's General row -- see the load effect. */}
          {/* NOT FOR WAX, PROPOLIS OR POLLEN. A varietal is the flower the
              nectar came from, so these products cannot have one -- and
              the server refuses the pair outright rather than ignoring
              it, which would make a stale picker value fail the sale. */}
          {showMoney && varietals.length > 0 && sendingHoney && (
            <Field label="Varietal">
              {(id, aria) => (
                <select id={id} {...aria} className="touch-target" value={assignForm.varietal_id}
                  onChange={(e) => setAssignForm((f) => ({ ...f, varietal_id: e.target.value }))}>
                  {/* "Not separated" was here and has been removed (28 Aug).
                      It was the DEFAULT as well as an option, so the
                      easiest path through this form wrote varietal_id
                      NULL -- the bucket V080 exists to drain. The guard
                      added earlier refused the submission but left the
                      option in place, which just moved the dead end from
                      the ledger to the form.

                      A placeholder rather than a value: the select now
                      starts on something that is plainly not an answer,
                      so the choice has to be made rather than defaulted
                      past. Same shape as the return dialog. */}
                  {/* THE PLACEHOLDER ONLY WHERE IT IS STILL AN ANSWER.
                      Reported once the form started landing on General:
                      "Probably need to remove that 'Choose...' entry from
                      the options list if possible." Right -- an option
                      whose only effect is to make the form refuse you is
                      a trap, not a choice, and it was reachable by
                      scrolling back up past a perfectly good default.
                      It stays for the account that has NO General row to
                      land on, where the select genuinely is unanswered
                      and must not render an option it is not holding. */}
                  {assignForm.varietal_id ? null : <option value="">Choose…</option>}
                  {/* THE COUNT MOVED TO THE SIZE (#401), and the reason
                      is the dependency rather than the layout.

                      It used to sit here: "Clover (3 on hand)", computed
                      from the size already chosen. But this is the FIRST
                      question on the form, so on opening there was no
                      size and every varietal stood bare -- the count
                      appeared only after the choice it was meant to
                      inform had been made.

                      The varietal narrows the SIZES. Same inversion #384
                      found for bottling, where the fix was to turn the
                      dependency round rather than swap the fields. */}
                  {varietals.filter((v) => v.is_active !== false).map((v) => (
                    <option key={v.varietal_id} value={v.varietal_id}>{v.name}</option>
                  ))}
                </select>
              )}
            </Field>
          )}
          {!sendingBulk ? (
            <>
              <Field label={sendingHoney ? "Bottle size" : "Size"}>
                {(id, aria) => (
                  <select id={id} {...aria} value={assignForm.bottle_size_id}
                    onChange={(e) => setAssignForm((f) => ({ ...f, bottle_size_id: e.target.value }))} className="touch-target">
                    <option value="">Select a size...</option>
                    {/* How many of the chosen honey are in each jar size.
                        The rule lives in services/bottledStock, shared
                        with mobile, and says nothing at all when the
                        shelf has not loaded -- a zero we did not verify
                        reads as "none left" and would stop somebody
                        sending jars they are holding. */}
                    {!sendingHoney ? (otherSizes[assignForm.mode.split(":")[1]] || []).map((z) => (
                      <option key={z.bottle_size_id} value={z.bottle_size_id}>
                        {`${z.label} (${z.on_hand} on the shelf)`}
                      </option>
                    )) : sizes.map((s) => (
                      <option key={s.bottle_size_id} value={s.bottle_size_id}>
                        {s.label + jarsLabel({
                          split: stock,
                          sizeId: s.bottle_size_id,
                          varietalId: assignForm.varietal_id
                            ? Number(assignForm.varietal_id)
                            : undefined,
                        })}
                      </option>
                    ))}
                  </select>
                )}
              </Field>
              <Field label={sendingHoney ? "How many bottles?" : "How many?"}>
                {(id, aria) => <input id={id} {...aria} type="number" min="1" value={assignForm.bottle_count}
                  onChange={(e) => setAssignForm((f) => ({ ...f, bottle_count: e.target.value }))} className="touch-target" />}
              </Field>
            </>
          ) : (
            <Field label="How many pounds?"
              hint={`Comes straight out of the ${sendingProduct} pool — bulk is never ${
                sendingHoney ? "bottled" : "packaged"}.`}>
              {(id, aria) => <input id={id} {...aria} type="number" step="0.1" min="0.1" value={assignForm.pounds}
                onChange={(e) => setAssignForm((f) => ({ ...f, pounds: e.target.value }))} className="touch-target" />}
            </Field>
          )}
          <Field
            label={sendingBulk ? "Price per pound" : "Price per bottle"}
            hint="Leave at 0 for a sample or giveaway. This price stays with the delivery, so a later change will not rewrite it."
          >
            {(id, aria) => <input id={id} {...aria} type="number" step="0.01" min="0" value={assignForm.unit_price}
              onChange={(e) => setAssignForm((f) => ({ ...f, unit_price: e.target.value }))} className="touch-target" />}
          </Field>
          <Field label="Note (optional)">
            {(id, aria) => <input id={id} {...aria} value={assignForm.notes}
              onChange={(e) => setAssignForm((f) => ({ ...f, notes: e.target.value }))} className="touch-target" />}
          </Field>
        </div>
      </Modal>

      {/* CONFIRMDIALOG, NOT window.confirm (#328). The guard that caught
          this states the reason better than a comment could: a native
          confirm can be switched off by the user, after which it returns
          false silently and the prompt stops appearing -- worst on
          exactly the irreversible actions it is usually protecting, and
          moving two years of invoices between ledgers is one of those.

          It NAMES BOTH and says which survives. "Are you sure" alone
          would not tell somebody which way round it goes, and getting
          that backwards is the mistake here with no undo. */}
      <ConfirmDialog
        open={confirmingMerge}
        onClose={() => setConfirmingMerge(false)}
        onConfirm={() => { setConfirmingMerge(false); doMerge(); }}
        title="Merge vendors"
        confirmLabel="Merge"
        danger
        busy={merging}
      >
        <p style={{ margin: 0 }}>
          Move everything from <strong>{data.vendor.name}</strong> into{" "}
          <strong>{mergeTargetName}</strong>?
        </p>
        <p style={{ marginBottom: 0 }}>
          <strong>{mergeTargetName}</strong> keeps all the history —
          deliveries, payments, returns and receipts.{" "}
          <strong>{data.vendor.name}</strong> is put away, and any share links
          for it are revoked.
        </p>
      </ConfirmDialog>

      <Modal open={payOpen} onClose={() => setPayOpen(false)} title={`Payment from ${data.vendor.name}`} footer={<Button onClick={submitPay}>Record Payment</Button>}>
        <div style={{ display: "grid", gap: "var(--space-3)" }}>
          <Field label="Amount" hint="A negative amount records a refund or a bounced payment.">
            {(id, aria) => <input id={id} {...aria} type="number" step="0.01" value={payForm.amount}
              onChange={(e) => setPayForm((f) => ({ ...f, amount: e.target.value }))} className="touch-target" />}
          </Field>
          {/* Picked, not typed (#196). This value is copied into a receipt's
              frozen snapshot, which is never re-derived -- so "Venmo",
              "venmo" and "VENMO" are not an untidy report, they are three
              permanent rows the vendor already holds a printed copy of.
              SelectOrAdd renders its own label. */}
          <SelectOrAdd
            listKey="payment_method"
            label="How they paid (optional)"
            value={payForm.method}
            onChange={(v) => setPayForm((f) => ({ ...f, method: v }))}
            placeholder="Not recorded"
          />
          <Field label="Note (optional)">
            {(id, aria) => <input id={id} {...aria} value={payForm.notes}
              onChange={(e) => setPayForm((f) => ({ ...f, notes: e.target.value }))} className="touch-target" />}
          </Field>
        </div>
      </Modal>

      <Modal open={glassOpen} onClose={() => setGlassOpen(false)} title={`Empty jars from ${data.vendor.name}`}
             footer={<Button onClick={submitGlass}>Log Empty Jars</Button>}>
        <div style={{ display: "grid", gap: "var(--space-3)" }}>
          <p style={{ marginTop: 0, color: "var(--color-text-muted)" }}>
            For glass handed back <strong>after the honey sold</strong>. This is a record only —
            it won't reduce what they owe you or add anything to your shelf.
            If they're returning <strong>unsold honey</strong>, use Accept Return instead.
          </p>
          <Field label="Bottle size">
            {(id, aria) => (
              <select id={id} {...aria} value={glassForm.bottle_size_id}
                onChange={(e) => setGlassForm((f) => ({ ...f, bottle_size_id: e.target.value }))} className="touch-target">
                <option value="">Select a size...</option>
                {/* Every size, not just what they hold -- see the button comment. */}
                {sizes.map((s2) => <option key={s2.bottle_size_id} value={s2.bottle_size_id}>{s2.label}</option>)}
              </select>
            )}
          </Field>
          <Field label="How many jars?">
            {(id, aria) => (
              <input id={id} {...aria} type="number" min="1" value={glassForm.bottle_count}
                onChange={(e) => setGlassForm((f) => ({ ...f, bottle_count: e.target.value }))} className="touch-target" />
            )}
          </Field>
          <Field label="Note (optional)" hint="e.g. 6 chipped, kept them">
            {(id, aria) => <input id={id} {...aria} value={glassForm.note}
              onChange={(e) => setGlassForm((f) => ({ ...f, note: e.target.value }))} className="touch-target" />}
          </Field>
        </div>
      </Modal>

      <Modal open={returnOpen} onClose={() => setReturnOpen(false)} title={`Return from ${data.vendor.name}`} footer={<Button onClick={submitReturn}>Accept Return</Button>}>
        <div style={{ display: "grid", gap: "var(--space-3)" }}>
          <Field label="What came back?">
            {(id, aria) => (
              <select id={id} {...aria} value={returnForm.mode}
                onChange={(e) => setReturnForm((f) => ({ ...f, mode: e.target.value }))} className="touch-target">
                <option value="bottled">Bottled honey</option>
                <option value="bulk">Bulk honey, by the pound</option>
              </select>
            )}
          </Field>
          {/* Which honey, BEFORE how much -- the order used on Send
              Honey, Correct Pool and everywhere else. Choosing the thing
              and then the quantity is the pattern; reversing it here
              would make one dialog read backwards.

              The old comment said a return inherits its varietal from
              the named delivery, so asking would invite a contradiction.
              True when a delivery IS named -- and the server still lets
              that win. But most returns are loose, a shop clearing a
              shelf with nobody remembering the drop, and those were
              landing with NO varietal at all. Asking somebody who is
              holding the jars to read the label is not a guess. */}
          {showMoney && varietals.length > 0 && (
            <Field label="Which honey?" hint="The label is on the jar. A named delivery takes precedence.">
              {(id, aria) => (
                <select id={id} {...aria} className="touch-target" value={returnForm.varietal_id}
                  onChange={(e) => setReturnForm((f) => ({ ...f, varietal_id: e.target.value }))}>
                  {/* "Not sure" was here and has been removed (27 Aug).
                      It created stock with no varietal, which could then
                      never be unbottled, corrected or counted against
                      its own size -- a bucket honey went into and
                      nothing came out of. For BOTTLED honey the answer
                      is printed on the jar, so the question is nearly
                      always answerable by whoever is holding it.
                      V080 drains what the option already created. */}
                  <option value="">Choose…</option>
                  {varietals.map((v) => (
                    <option key={v.varietal_id} value={v.varietal_id}>{v.name}</option>
                  ))}
                </select>
              )}
            </Field>
          )}
          {returnForm.mode === "bottled" ? (
            <>
              <Field label="Bottle size">
                {(id, aria) => (
                  <select id={id} {...aria} value={returnForm.bottle_size_id}
                    onChange={(e) => setReturnForm((f) => ({ ...f, bottle_size_id: e.target.value }))} className="touch-target">
                    <option value="">Select a size...</option>
                    {/* Only what they actually hold -- returning a size they
                        never had is a mis-selection, and the API refuses it. */}
                    {/* Collapsed back to one row per size. A return names
                        the delivery it came from and inherits ITS varietal
                        (V064), so asking again here would invite an answer
                        that contradicts the one on record. */}
                    {Object.values(
                      (data.holdings || []).reduce((acc, h) => {
                        const key = h.bottle_size_id;
                        acc[key] = acc[key]
                          ? { ...acc[key], bottles: acc[key].bottles + h.bottles }
                          : { bottle_size_id: h.bottle_size_id, label: h.label, bottles: h.bottles };
                        return acc;
                      }, {}),
                    ).map((h) => (
                      <option key={h.bottle_size_id} value={h.bottle_size_id}>{h.label} ({h.bottles} out)</option>
                    ))}
                  </select>
                )}
              </Field>
              <Field label="How many came back?">
                {(id, aria) => <input id={id} {...aria} type="number" min="1" value={returnForm.bottle_count}
                  onChange={(e) => setReturnForm((f) => ({ ...f, bottle_count: e.target.value }))} className="touch-target" />}
              </Field>
            </>
          ) : (
            <Field label="How many pounds?">
              {(id, aria) => <input id={id} {...aria} type="number" step="0.1" min="0.1" value={returnForm.pounds}
                onChange={(e) => setReturnForm((f) => ({ ...f, pounds: e.target.value }))} className="touch-target" />}
            </Field>
          )}
          <Field
            label="Condition"
            hint="Damaged stock still comes off what they owe, but is not put back into inventory."
          >
            {(id, aria) => (
              <select id={id} {...aria} value={returnForm.condition}
                onChange={(e) => setReturnForm((f) => ({ ...f, condition: e.target.value }))} className="touch-target">
                <option value="Good">Good — back into stock</option>
                <option value="Damaged">Damaged — write off</option>
              </select>
            )}
          </Field>
          <Field label="Note (optional)">
            {(id, aria) => <input id={id} {...aria} value={returnForm.notes}
              onChange={(e) => setReturnForm((f) => ({ ...f, notes: e.target.value }))} className="touch-target" />}
          </Field>
        </div>
      </Modal>

      <Modal
        open={soldFor !== null}
        onClose={() => setSoldFor(null)}
        title={soldFor ? `Sold — ${soldFor.label}` : "Sold"}
        footer={<Button onClick={recordSale}>Mark sold</Button>}
      >
        {/* Says the thing that is easy to assume and wrong: this moves
            stock, not money. The charge happened when the honey went out. */}
        <p style={{ marginTop: 0, color: "var(--color-text-muted)" }}>
          Takes the jars off their shelf. It does not change what they owe — that
          was set when you sent them the honey.
        </p>
        <Field label={soldFor ? `How many of the ${soldFor.bottles} they hold?` : "How many?"}>
          {(id, aria) => (
            <input id={id} {...aria} type="number" min="1" max={soldFor?.bottles}
              className="touch-target" value={soldCount}
              onChange={(e) => setSoldCount(e.target.value)} />
          )}
        </Field>

        {/* Money taken while you were standing there. Optional, because
            plenty of sales are reported without any changing hands -- but
            when it does, sending somebody to a second form for it makes
            one errand into two. */}
        <Field
          label="Collected anything? (optional)"
          hint="Recorded as a payment against their balance, not as a price on these jars."
        >
          {(id, aria) => (
            <input id={id} {...aria} type="number" min="0" step="0.01"
              className="touch-target" value={soldCollected}
              placeholder="0.00"
              onChange={(e) => setSoldCollected(e.target.value)} />
          )}
        </Field>

        {/* Only once there is money to describe. "How was it paid" with
            nothing paid is a question about nothing. */}
        {Number(soldCollected) > 0 && (
          <Field label="How?">
            {(id, aria) => (
              <select id={id} {...aria} className="touch-target" value={soldMethod}
                onChange={(e) => setSoldMethod(e.target.value)}>
                <option value="">Not saying</option>
                <option value="Cash">Cash</option>
                <option value="Check">Check</option>
                <option value="Transfer">Transfer</option>
                <option value="Card">Card</option>
              </select>
            )}
          </Field>
        )}
      </Modal>
    </div>
  );
}
